import { useRef, useCallback } from "react";
import { motion } from "motion/react";
import gsap from "gsap";
import { steps, type ProcessStep } from "../data/process-steps";
import "../styles/process.css";

/* ─── DIRECTIONAL CARD VARIANTS ─────────────── */
const cardDirections = [
  { x: -90, y: 30, rotate: -4 },
  { x: 0, y: 100, scale: 0.9 },
  { x: 90, y: 30, rotate: 4 },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.14 } },
};

/* ─── SECTION HEADER ────────────────────────── */
function SectionHeader() {
  return (
    <div className="mb-16 md:mb-20">
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="font-Montserrat text-xs tracking-widest uppercase text-indigo-600 dark:text-indigo-300/70 mb-6 flex items-center gap-4"
      >
        <div className="w-4 h-px bg-indigo-500/50" />
        The Kivas Way
      </motion.div>

      <motion.h1
        initial={{ y: 40, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
        className="font-Montserrat text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-black dark:text-white"
      >
        How We{" "}
        <motion.span
          initial={{ y: 40, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1], delay: 0.25 }}
          className="inline-block text-transparent bg-clip-text bg-gradient-to-r from-black dark:from-white via-indigo-600 dark:via-indigo-200 to-indigo-500/50 italic font-serif"
        >
          Built
        </motion.span>
      </motion.h1>
    </div>
  );
}

/* ─── STEP CARD ─────────────────────────────── */
function StepCard({ step, index }) {
  const cardRef = useRef(null);
  const titleRef = useRef(null);
  const orbRef = useRef(null);

  const cardVariants = {
    hidden: { opacity: 0, ...cardDirections[index] },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      rotate: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 60,
        damping: 14,
        opacity: { duration: 0.55, ease: "easeOut" },
      },
    },
  };

  /* ── Orb parallax on mouse move ── */
  const handleMouseMove = useCallback((e) => {
    const r = cardRef.current.getBoundingClientRect();
    const mx = (e.clientX - r.left - r.width / 2) / r.width;
    const my = (e.clientY - r.top - r.height / 2) / r.height;
    gsap.to(orbRef.current, {
      x: mx * 35,
      y: my * 35,
      duration: 0.6,
      ease: "power2.out",
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    gsap.to(orbRef.current, { x: 0, y: 0, duration: 1, ease: "expo.out" });
    gsap.to(titleRef.current, { color: "#f0f0f2", duration: 0.3 });
  }, []);

  const handleMouseEnter = useCallback(() => {
    gsap.to(titleRef.current, {
      color: step.accent,
      duration: 0.35,
      ease: "power2.out",
    });
  }, [step.accent]);

  return (
    <motion.div
      ref={cardRef}
      className="kp-card"
      variants={cardVariants}
      whileHover={{ y: -6 }}
      style={
        {
          "--card-accent-border": `rgba(${step.accentRgb}, 0.25)`,
        } as React.CSSProperties
      }
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
    >
      <div className="kp-card-noise" />

      {/* Orb */}
      <div
        ref={orbRef}
        className="kp-card-orb"
        style={{
          background: `radial-gradient(circle, rgba(${step.accentRgb}, 0.28) 0%, transparent 70%)`,
        }}
      />

      {/* Top row */}
      <div className="kp-card-top">
        <div className="kp-phase-badge">
          <div
            className="kp-phase-dot"
            style={{ background: step.accent, color: step.accent }}
          />
          <span className="kp-phase-num" style={{ color: step.accent }}>
            Phase {step.phase}
          </span>
        </div>
        <span
          className="kp-tag"
          style={{
            color: "#fff",
            background: "rgba(255,255,255,0.08)",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(255,255,255,0.15)",
          }}
        >
          {step.tag}
        </span>
      </div>

      {/* Title */}
      <h3 ref={titleRef} className="kp-card-title">
        {step.title}
      </h3>

      {/* Accent line — drawn after card enters */}
      <motion.div
        className="kp-card-line"
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.55, ease: "easeOut", delay: 0.15 }}
        style={{
          background:
            `linear-gradient(90deg, ${step.accent}, transparent)` as any,
        }}
      />

      {/* Description */}
      <motion.p
        className="kp-card-desc text-justify"
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
      >
        {step.desc}
      </motion.p>

      {/* Hover bar */}
      <div className="kp-card-bar">
        <div
          className="kp-card-bar-fill"
          style={{
            background: `linear-gradient(90deg, ${step.accent}, rgba(${step.accentRgb}, 0.2))`,
          }}
        />
      </div>
    </motion.div>
  );
}

/* ─── ROOT ──────────────────────────────────── */
export default function Process() {
  return (
    <section className="kivas-process" id="process">
      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 relative z-10">
        <SectionHeader />

        <motion.div
          className="kp-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {steps.map((step, index) => (
            <StepCard key={step.phase} step={step} index={index} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}
